import { useParams } from "react-router-dom";

import { ApiError } from "../api/client";
import { errorMessage, useCancelJob, useJob, useJobLogs } from "../api/queries";
import { TERMINAL_JOB_STATES } from "../api/types";
import { useSession } from "../auth/SessionProvider";
import { ClusterProblem, isClusterProblem } from "../components/ClusterProblem";
import { WaitingList } from "../components/capacity/WaitingSection";
import { PageHeader } from "../components/layout/PageHeader";
import { ErrorBlock, LoadingBlock } from "../components/ui/StateBlock";
import { EventTimeline } from "../components/workspace/EventTimeline";
import { formatDateTime, pluralize } from "../lib/format";
import { rowsFromWaiting, waitingNote } from "../lib/waiting";
import { jobHeadline, jobStateLabel, jobTone } from "../lib/workload";

/**
 * One job: its state, its log, and a way to stop it.
 *
 * Returns:
 *     The job screen.
 */
export function JobDetailPage(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const job = useJob(id);
  const { me } = useSession();
  const cancel = useCancelJob(id);
  const logs = useJobLogs(id, Boolean(job.data));

  const running =
    job.data !== undefined && !TERMINAL_JOB_STATES.includes(job.data.state);

  const header = (
    <PageHeader
      eyebrow="Batch"
      title={job.data?.name ?? "Job"}
      back={{ to: "/jobs", label: "Jobs" }}
      subtitle={job.data ? job.data.image : undefined}
      actions={
        running ? (
          <button
            type="button"
            className="btn btn--danger"
            onClick={() => cancel.mutate()}
            disabled={cancel.isPending}
          >
            {cancel.isPending ? "Cancelling…" : "Cancel"}
          </button>
        ) : null
      }
    />
  );

  if (job.isPending) {
    return (
      <>
        {header}
        <LoadingBlock label="Loading this job…" />
      </>
    );
  }

  if (job.isError) {
    if (isClusterProblem(job.error)) {
      return (
        <>
          {header}
          <ClusterProblem error={job.error} onRetry={() => void job.refetch()} />
        </>
      );
    }
    if (job.error instanceof ApiError && job.error.isNotFound) {
      return (
        <>
          {header}
          <p className="state-block">
            This job does not exist any more, or it belongs to somebody else.
          </p>
        </>
      );
    }
    return (
      <>
        {header}
        <ErrorBlock error={job.error} onRetry={() => void job.refetch()} />
      </>
    );
  }

  const data = job.data;

  return (
    <>
      {header}

      <section className="state-line" data-tone={jobTone(data.state)}>
        <span className="badge" data-tone={jobTone(data.state)}>
          <span className="badge__dot" aria-hidden="true" />
          {jobStateLabel(data.state)}
        </span>
        <p className="state-line__text">{jobHeadline(data)}</p>
        {data.waiting ? (
          <p className="state-line__note">{waitingNote(data.waiting, "GPU")}</p>
        ) : null}
      </section>

      {data.waiting && data.waiting.ahead.length > 0 ? (
        <WaitingList
          rows={rowsFromWaiting(data.waiting, {
            id: data.id,
            user: data.user,
            gpus: data.gpus,
            cores: null,
          })}
          unit="GPU"
          you={me.username}
        />
      ) : null}

      {cancel.isError ? (
        <p className="form__error" role="alert">
          {errorMessage(cancel.error)}
        </p>
      ) : null}

      <section className="section">
        <p className="job-detail__meta">
          {pluralize(data.gpus, "GPU")} · submitted {formatDateTime(data.created_at)}
          {data.node ? (
            <>
              {" · "}
              <span className="mono">{data.node}</span>
            </>
          ) : null}
        </p>
        <pre className="log log--command">{data.command}</pre>
      </section>

      <section className="section">
        <h2 className="section__title">What has happened</h2>
        <EventTimeline events={data.events} />
      </section>

      <section className="section">
        <h2 className="section__title">Log</h2>
        {logs.isPending ? (
          <LoadingBlock label="Reading the log…" />
        ) : logs.isError ? (
          <ErrorBlock error={logs.error} onRetry={() => void logs.refetch()} />
        ) : (
          <pre className="log">{logs.data || "Nothing has been written yet."}</pre>
        )}
      </section>
    </>
  );
}
